import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { Permission } from '../../../../permissions';
import { errorToast, successToast } from '../../../Toast';
import {
	ROLES_TOAST_ADD_FAILURE,
	ROLES_TOAST_ADD_SUCCESS,
	ROLES_TOAST_DELETE_FAILURE,
	ROLES_TOAST_DELETE_SUCCESS,
	ROLES_TOAST_UPDATE_FAILURE,
	ROLES_TOAST_UPDATE_SUCCESS,
} from '../data/constants';
import apiClient from './apiClient';

const ROLES_KEY = 'roles';

export interface RoleResponse {
	roles: Role[];
}
export interface Role {
	name: string;
	displayName: string;
	permissions: Permission[];
}

export interface RoleCreateRequest {
	name: string;
	displayName: string;
	permissions: Permission[];
}

export interface RoleUpdateRequest {
	name: string;
	displayName: string;
	assignPermissions?: Permission[];
	unassignPermissions?: Permission[];
	replaceAll?: boolean; // defaults to false; if true assignPermissions replaces existing permissions
}

const deleteRole = async (roleName: string, authToken: string) => {
	const res = await apiClient.delete<RoleResponse>(`/roles/${roleName}`, {
		headers: {
			Authorization: authToken,
		},
	});
	return res.data;
};

const deleteRoleSso = async (roleName: string, tenantLocator: string) => {
	const res = await apiClient.delete<RoleResponse>(
		`/roles/${roleName}?ns_tenant_host=${tenantLocator}`,
	);
	return res.data;
};

const getRolesSso = async (tenantLocator: string) => {
	const res = await apiClient.get<RoleResponse>(
		`/roles?ns_tenant_host=${tenantLocator}`,
	);
	return res.data;
};

const getRoles = async (authToken: string) => {
	const res = await apiClient.get<RoleResponse>(`/roles`, {
		headers: {
			Authorization: authToken,
		},
	});
	return res.data;
};

const updateRole = async (request: RoleUpdateRequest, authToken: string) => {
	const res = await apiClient.patch<RoleResponse>(
		`/roles/${request.name}`,
		request,
		{
			headers: {
				Authorization: authToken,
			},
		},
	);
	return res.data;
};

const updateRoleSso = async (
	request: RoleUpdateRequest,
	tenantLocator: string,
) => {
	const res = await apiClient.patch<RoleResponse>(
		`/roles/${request.name}?ns_tenant_host=${tenantLocator}`,
		request,
	);
	return res.data;
};

const createRole = async (request: RoleCreateRequest, authToken: string) => {
	const res = await apiClient.post<RoleResponse>(`/roles`, request, {
		headers: {
			Authorization: authToken,
		},
	});
	return res.data;
};

const createRoleSso = async (
	request: RoleCreateRequest,
	tenantLocator: string,
) => {
	const res = await apiClient.post<RoleResponse>(
		`/roles?ns_tenant_host=${tenantLocator}`,
		request,
	);
	return res.data;
};

// Queries
const useRoles = (
	queryKey: string[],
	tenantLocator?: string,
	token?: string,
) => {
	return useQuery({
		queryKey,
		queryFn: async () => {
			const response = tenantLocator
				? await getRolesSso(tenantLocator)
				: await getRoles(token);
			return response;
		},
	});
};

const useCreateRole = (
	queryKey: string[],
	tenantLocator?: string,
	token?: string,
	successCallback?: () => void,
) => {
	const client = useQueryClient();
	return useMutation(
		(newRole: RoleCreateRequest) => {
			return tenantLocator
				? createRoleSso(newRole, tenantLocator)
				: createRole(newRole, token);
		},
		{
			onSuccess: () => {
				client.invalidateQueries(queryKey);
				successToast(ROLES_TOAST_ADD_SUCCESS());
				successCallback && successCallback();
			},
			onError: (err) => {
				if (axios.isAxiosError(err) && err.response.data.message) {
					errorToast(
						ROLES_TOAST_ADD_FAILURE() + ': ' + err.response.data.message,
					);
				} else {
					errorToast(ROLES_TOAST_ADD_FAILURE());
				}
			},
			onSettled: () => {
				client.invalidateQueries(queryKey);
			},
		},
	);
};

const useUpdateRole = (
	queryKey: string[],
	tenantLocator?: string,
	token?: string,
	successCallback?: () => void,
) => {
	const client = useQueryClient();
	return useMutation(
		(updatedRole: RoleUpdateRequest) => {
			return tenantLocator
				? updateRoleSso(updatedRole, tenantLocator)
				: updateRole(updatedRole, token);
		},
		{
			onSuccess: () => {
				client.invalidateQueries(queryKey);
				successToast(ROLES_TOAST_UPDATE_SUCCESS());
				successCallback && successCallback();
			},
			onError: (err) => {
				if (axios.isAxiosError(err) && err.response.data.message) {
					errorToast(
						ROLES_TOAST_UPDATE_FAILURE() + ': ' + err.response.data.message,
					);
				} else {
					errorToast(ROLES_TOAST_UPDATE_FAILURE());
				}
			},
			onSettled: () => {
				client.invalidateQueries(queryKey);
			},
		},
	);
};

const useDeleteRole = (
	queryKey: string[],
	tenantLocator?: string,
	token?: string,
	successCallback?: () => void,
) => {
	const client = useQueryClient();
	return useMutation(
		(roleName: string) => {
			return tenantLocator
				? deleteRoleSso(roleName, tenantLocator)
				: deleteRole(roleName, token);
		},
		{
			onSuccess: () => {
				client.invalidateQueries(queryKey);
				successToast(ROLES_TOAST_DELETE_SUCCESS());
				successCallback && successCallback();
			},
			onError: (err) => {
				if (axios.isAxiosError(err) && err.response.data.message) {
					errorToast(
						ROLES_TOAST_DELETE_FAILURE() + ': ' + err.response.data.message,
					);
				} else {
					errorToast(ROLES_TOAST_DELETE_FAILURE());
				}
			},
			onSettled: () => {
				client.invalidateQueries(queryKey);
			},
		},
	);
};

const RolesAPI = {
	getRoles,
	getRolesSso,
	createRole,
	createRoleSso,
	useRoles,
	useCreateRole,
	useUpdateRole,
	useDeleteRole,
	ROLES_KEY,
};

export default RolesAPI;
